import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, Mail, Facebook, Instagram, Youtube } from 'lucide-react';
import axios from 'axios';
import CaptchaWidget from '../components/CaptchaWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', location: '', message: '' });
  const [captcha, setCaptcha] = useState({ answer: '', token: '' });
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { axios.get(`${API}/locations`).then(res => setLocations(res.data)); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!captcha.answer) { setError('Please solve the captcha.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...formData, captcha_answer: captcha.answer, captcha_token: captcha.token });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-uf-red font-heading text-xl tracking-[0.3em] mb-2">GET IN TOUCH</p>
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white">Contact Us</h1>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="font-heading text-3xl font-bold text-white mb-6">Send Us a Message</h2>
              {success ? (
                <div className="bg-green-500/10 border border-green-500/50 text-green-400 px-6 py-8 text-center"><Check size={48} className="mx-auto mb-4" /><p className="text-lg">Thank you! We'll be in touch soon.</p></div>
              ) : (
                <form onSubmit={handleSubmit} className="uf-form space-y-6" data-testid="contact-form">
                  {error && <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 text-sm">{error}</div>}
                  <input type="text" placeholder="Your Name" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required data-testid="contact-name" />
                  <input type="email" placeholder="Email Address" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required data-testid="contact-email" />
                  <input type="tel" placeholder="Phone Number" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} data-testid="contact-phone" />
                  <select value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} required data-testid="contact-location">
                    <option value="">Select Location</option>
                    {locations.map(loc => <option key={loc.id} value={loc.name}>{loc.name}</option>)}
                  </select>
                  <textarea placeholder="Your Message" rows={4} value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} required className="resize-none" data-testid="contact-message" />
                  <CaptchaWidget onVerify={(answer, token) => setCaptcha({ answer, token })} />
                  <button type="submit" className="btn-primary" disabled={loading} data-testid="contact-submit"><span>{loading ? 'Sending...' : 'Send Message'}</span></button>
                </form>
              )}
            </div>
            <div>
              <h2 className="font-heading text-3xl font-bold text-white mb-6">Contact Information</h2>
              <div className="space-y-6">
                <div className="bg-uf-surface border border-uf-border p-6">
                  <h3 className="font-heading text-xl font-bold text-white mb-4">General Enquiries</h3>
                  <a href="mailto:info@ultraflexgym.co.uk" className="flex items-center gap-3 text-uf-textMuted hover:text-uf-red transition-colors"><Mail size={18} />info@ultraflexgym.co.uk</a>
                </div>
                <div className="bg-uf-surface border border-uf-border p-6">
                  <h3 className="font-heading text-xl font-bold text-white mb-4">Membership Enquiries</h3>
                  <a href="mailto:memberships@ultraflexgym.co.uk" className="flex items-center gap-3 text-uf-textMuted hover:text-uf-red transition-colors"><Mail size={18} />memberships@ultraflexgym.co.uk</a>
                </div>
                <div className="bg-uf-surface border border-uf-border p-6">
                  <h3 className="font-heading text-xl font-bold text-white mb-4">Follow Us</h3>
                  <div className="flex gap-4">
                    <a href="https://www.facebook.com/UltraFlexGyms" target="_blank" rel="noopener noreferrer" className="text-uf-textMuted hover:text-uf-red transition-colors"><Facebook size={24} /></a>
                    <a href="https://www.instagram.com/ultraflexgyms" target="_blank" rel="noopener noreferrer" className="text-uf-textMuted hover:text-uf-red transition-colors"><Instagram size={24} /></a>
                    <a href="https://www.youtube.com/channel/UCIAR2QqxmCILBFLoHIIAPvA" target="_blank" rel="noopener noreferrer" className="text-uf-textMuted hover:text-uf-red transition-colors"><Youtube size={24} /></a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
